import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../api/api';
import {
  CRM_FOLLOW_UP_FILTERS,
  CRM_PIPELINE_STAGES,
  CRM_TASK_PRIORITIES,
  CrmFollowUpFilter,
  priorityLabel,
  stageLabel,
  toLocalInputValue,
  fromLocalInputValue,
} from '../../utils/crmPipeline';

interface Manager {
  id: string;
  name: string;
}

interface CustomerSummary {
  id: number;
  name: string;
  contacts: string;
  notes: string;
  isActive: boolean;
  stageCode: string;
  stageName: string;
  assignedUserId: string | null;
  assignedUserName: string | null;
  nextContactAtUtc?: string | null;
  isFollowUpOverdue: boolean;
  orderCount: number;
  completedOrderCount: number;
  completedRevenue: number;
  averageCompletedCheck: number;
  lastEventDate?: string | null;
  nextEventDate?: string | null;
  pendingTaskCount: number;
  overdueTaskCount: number;
  version: number;
}

interface CustomerOrder {
  id: number;
  eventDate: string;
  eventName: string;
  eventVenue: string;
  guestCount: number;
  orderStatusId: number;
  orderStatus: string;
  totalPrice: number;
}

interface CustomerTask {
  id: number;
  crmCustomerId: number;
  title: string;
  notes: string;
  dueAtUtc?: string | null;
  priorityCode: string;
  priorityName: string;
  isCompleted: boolean;
  completedAtUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  assignedUserId: string | null;
  assignedUserName: string | null;
  createdByUserId: string | null;
  createdByUserName: string | null;
  isOverdue: boolean;
  version: number;
}

interface CustomerDetails extends CustomerSummary {
  createdAtUtc: string;
  updatedAtUtc: string;
  orders: CustomerOrder[];
  tasks: CustomerTask[];
}

const money = (value: number) => `${(value || 0).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;
const date = (value?: string | null) => value ? new Date(value).toLocaleDateString('uk-UA') : '—';
const dateTime = (value?: string | null) => value ? new Date(value).toLocaleString('uk-UA') : '—';

const stageBadgeColor = (stageCode: string): { bg: string; fg: string } => {
  switch (stageCode) {
    case 'won': return { bg: 'var(--crm-success-bg)', fg: 'var(--crm-success)' };
    case 'lost': return { bg: 'var(--crm-danger-bg)', fg: 'var(--crm-danger)' };
    case 'proposal': return { bg: 'var(--crm-warning-bg)', fg: 'var(--crm-warning)' };
    case 'qualified': return { bg: 'var(--crm-info-bg)', fg: 'var(--crm-info)' };
    case 'contacted': return { bg: 'var(--crm-info-bg)', fg: 'var(--crm-info)' };
    default: return { bg: 'var(--crm-surface-alt)', fg: 'var(--crm-text-muted)' };
  }
};

const StageBadge: React.FC<{ code: string; name: string }> = ({ code, name }) => {
  const colors = stageBadgeColor(code);
  return <span style={{ padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: colors.bg, color: colors.fg, whiteSpace: 'nowrap' }}>{name}</span>;
};

const OverdueBadge: React.FC<{ count: number; label: string }> = ({ count, label }) => count > 0
  ? <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)' }}>{label}: {count}</span>
  : null;

type ButtonTone = 'success' | 'info' | 'danger' | 'muted' | 'primary';
const buttonStyle = (tone: ButtonTone, extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: tone === 'primary' ? 'var(--crm-navbar-dropdown-bg)' : tone === 'muted' ? 'var(--crm-surface-alt)' : `var(--crm-${tone}-bg)`,
  color: tone === 'primary' ? 'var(--crm-primary-contrast)' : tone === 'muted' ? 'var(--crm-text-muted)' : `var(--crm-${tone})`,
  border: `1px solid var(--crm-${tone === 'primary' || tone === 'muted' ? 'border' : tone})`,
  borderRadius: 'var(--crm-radius-md)',
  cursor: 'pointer',
  ...extra,
});
const fieldStyle: React.CSSProperties = {
  color: 'var(--crm-text)',
  background: 'var(--crm-input-bg)',
  border: '1px solid var(--crm-border)',
  borderRadius: 'var(--crm-radius-md)',
};

type CustomerEditState = {
  name: string;
  contacts: string;
  notes: string;
  isActive: boolean;
  stageCode: string;
  assignedUserId: string;
  nextContactAtUtcLocal: string;
};

type TaskDraft = { title: string; notes: string; dueAtUtcLocal: string; priorityCode: string; assignedUserId: string };

const emptyTaskDraft: TaskDraft = { title: '', notes: '', dueAtUtcLocal: '', priorityCode: 'normal', assignedUserId: '' };

const CrmCustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<CustomerSummary[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [followUpFilter, setFollowUpFilter] = useState<CrmFollowUpFilter | ''>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('active');

  const [selected, setSelected] = useState<CustomerDetails | null>(null);
  const [edit, setEdit] = useState<CustomerEditState>({ name: '', contacts: '', notes: '', isActive: true, stageCode: 'lead', assignedUserId: '', nextContactAtUtcLocal: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [taskError, setTaskError] = useState('');
  const [taskDraft, setTaskDraft] = useState<TaskDraft>(emptyTaskDraft);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [taskEdit, setTaskEdit] = useState<TaskDraft>(emptyTaskDraft);

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedCustomerId = searchParams.get('customerId');
  // Remembers which deep link was already handled, so a card opens exactly once per customerId.
  const handledCustomerIdRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get<CustomerSummary[]>('/crm-customers', {
        params: {
          search: appliedSearch || undefined,
          stage: stageFilter || undefined,
          assignedUserId: managerFilter || undefined,
          followUp: followUpFilter || undefined,
          isActive: activeFilter === 'all' ? undefined : activeFilter === 'active',
        },
      });
      setCustomers(response.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося завантажити клієнтів.');
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, stageFilter, managerFilter, followUpFilter, activeFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get<Manager[]>('/crm-customers/managers')
      .then(response => setManagers(response.data))
      .catch((e: any) => setError(e?.response?.data?.message || 'Не вдалося завантажити список менеджерів.'));
  }, []);


  const openCustomer = async (id: number) => {
    setError('');
    setTaskError('');
    setEditingTaskId(null);
    setTaskDraft(emptyTaskDraft);
    try {
      const response = await api.get<CustomerDetails>(`/crm-customers/${id}`);
      applyDetails(response.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося відкрити картку клієнта.');
    }
  };

  const applyDetails = (details: CustomerDetails) => {
    setSelected(details);
    setEdit({
      name: details.name,
      contacts: details.contacts,
      notes: details.notes,
      isActive: details.isActive,
      stageCode: details.stageCode,
      assignedUserId: details.assignedUserId || '',
      nextContactAtUtcLocal: toLocalInputValue(details.nextContactAtUtc),
    });
  };

  // Closes the card and drops the customerId deep link from the URL, keeping other query state intact.
  const closeCustomer = () => {
    setSelected(null);
    if (searchParams.has('customerId')) {
      const next = new URLSearchParams(searchParams);
      next.delete('customerId');
      setSearchParams(next, { replace: true });
    }
  };

  // Opens the card requested via /management/customers?customerId=<id> once, without touching the filters.
  useEffect(() => {
    if (!requestedCustomerId) {
      // Link consumed (or never present): allow the same customer to be opened again later.
      handledCustomerIdRef.current = null;
      return;
    }
    if (handledCustomerIdRef.current === requestedCustomerId) return;
    handledCustomerIdRef.current = requestedCustomerId;
    const customerId = Number(requestedCustomerId);
    if (!Number.isInteger(customerId) || customerId <= 0) {
      setError('Некоректний ідентифікатор клієнта в посиланні.');
      return;
    }
    openCustomer(customerId);
    // openCustomer is recreated on every render; the ref guard keeps this effect single-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedCustomerId]);

  const save = async () => {
    if (!selected) return;
    try {
      const response = await api.put<CustomerDetails>(`/crm-customers/${selected.id}`, {
        name: edit.name,
        contacts: edit.contacts,
        notes: edit.notes,
        isActive: edit.isActive,
        stageCode: edit.stageCode,
        assignedUserId: edit.assignedUserId || null,
        nextContactAtUtc: fromLocalInputValue(edit.nextContactAtUtcLocal, selected.nextContactAtUtc),
      }, { headers: { 'If-Match': `"${selected.version}"` } });
      applyDetails(response.data);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося зберегти клієнта.');
    }
  };

  const refreshSelectedTasks = async () => {
    if (!selected) return;
    const response = await api.get<CustomerDetails>(`/crm-customers/${selected.id}`);
    applyDetails(response.data);
    await load();
  };

  const createTask = async () => {
    if (!selected) return;
    setTaskError('');
    if (!taskDraft.title.trim()) {
      setTaskError('Назва завдання обов’язкова.');
      return;
    }
    try {
      await api.post(`/crm-customers/${selected.id}/tasks`, {
        title: taskDraft.title,
        notes: taskDraft.notes,
        dueAtUtc: fromLocalInputValue(taskDraft.dueAtUtcLocal),
        priorityCode: taskDraft.priorityCode,
        assignedUserId: taskDraft.assignedUserId || null,
      });
      setTaskDraft(emptyTaskDraft);
      await refreshSelectedTasks();
    } catch (e: any) {
      setTaskError(e?.response?.data?.message || 'Не вдалося створити завдання.');
    }
  };

  const startEditTask = (task: CustomerTask) => {
    setEditingTaskId(task.id);
    setTaskEdit({
      title: task.title,
      notes: task.notes,
      dueAtUtcLocal: toLocalInputValue(task.dueAtUtc),
      priorityCode: task.priorityCode,
      assignedUserId: task.assignedUserId || '',
    });
  };

  const saveTask = async (task: CustomerTask) => {
    if (!selected) return;
    setTaskError('');
    if (!taskEdit.title.trim()) {
      setTaskError('Назва завдання обов’язкова.');
      return;
    }
    try {
      await api.put(`/crm-customers/${selected.id}/tasks/${task.id}`, {
        title: taskEdit.title,
        notes: taskEdit.notes,
        dueAtUtc: fromLocalInputValue(taskEdit.dueAtUtcLocal, task.dueAtUtc),
        priorityCode: taskEdit.priorityCode,
        assignedUserId: taskEdit.assignedUserId || null,
      }, { headers: { 'If-Match': `"${task.version}"` } });
      setEditingTaskId(null);
      await refreshSelectedTasks();
    } catch (e: any) {
      setTaskError(e?.response?.data?.message || 'Не вдалося зберегти завдання.');
    }
  };

  const toggleTaskCompletion = async (task: CustomerTask) => {
    if (!selected) return;
    setTaskError('');
    try {
      const action = task.isCompleted ? 'reopen' : 'complete';
      await api.post(`/crm-customers/${selected.id}/tasks/${task.id}/${action}`, null, { headers: { 'If-Match': `"${task.version}"` } });
      await refreshSelectedTasks();
    } catch (e: any) {
      setTaskError(e?.response?.data?.message || 'Не вдалося оновити статус завдання.');
    }
  };

  const deleteTask = async (task: CustomerTask) => {
    if (!selected) return;
    if (!window.confirm('Видалити завдання?')) return;
    setTaskError('');
    try {
      await api.delete(`/crm-customers/${selected.id}/tasks/${task.id}`, { headers: { 'If-Match': `"${task.version}"` } });
      await refreshSelectedTasks();
    } catch (e: any) {
      setTaskError(e?.response?.data?.message || 'Не вдалося видалити завдання.');
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto', color: 'var(--crm-text)' }}>
      <h2 style={{ marginTop: 0 }}>CRM клієнтів</h2>
      <p style={{ color: 'var(--crm-text-muted)' }}>Воронка продажів, відповідальні менеджери, наступні контакти та завдання по кожному клієнту.</p>

      <form className="crm-customer-filters" onSubmit={event => { event.preventDefault(); setAppliedSearch(search.trim()); }} style={{ display: 'flex', gap: 8, margin: '18px 0', flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Пошук за ім’ям, телефоном або email" style={{ ...fieldStyle, minWidth: 280, padding: '9px 11px' }} />
        <button type="submit" style={buttonStyle('info', { padding: '9px 18px' })}>Знайти</button>
        {appliedSearch && <button type="button" onClick={() => { setSearch(''); setAppliedSearch(''); }} style={buttonStyle('muted', { padding: '9px 18px' })}>Скинути</button>}

        <select value={stageFilter} onChange={event => setStageFilter(event.target.value)} style={{ ...fieldStyle, padding: '9px 11px' }}>
          <option value="">Усі стадії</option>
          {CRM_PIPELINE_STAGES.map(stage => <option key={stage} value={stage}>{stageLabel(stage)}</option>)}
        </select>

        <select value={managerFilter} onChange={event => setManagerFilter(event.target.value)} style={{ ...fieldStyle, padding: '9px 11px' }}>
          <option value="">Усі менеджери</option>
          {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>

        <select value={followUpFilter} onChange={event => setFollowUpFilter(event.target.value as CrmFollowUpFilter | '')} style={{ ...fieldStyle, padding: '9px 11px' }}>
          <option value="">Будь-який контакт</option>
          {CRM_FOLLOW_UP_FILTERS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>

        <select value={activeFilter} onChange={event => setActiveFilter(event.target.value as 'all' | 'active' | 'inactive')} style={{ ...fieldStyle, padding: '9px 11px' }}>
          <option value="active">Активні</option>
          <option value="inactive">Неактивні</option>
          <option value="all">Усі</option>
        </select>
      </form>

      {error && <div role="alert" style={{ padding: 12, marginBottom: 14, background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 6 }}>{error}</div>}

      <div style={{ overflowX: 'auto', border: '1px solid var(--crm-border)', borderRadius: 8, background: 'var(--crm-surface)' }}>
        <table style={{ width: '100%', minWidth: 1050, borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: 'var(--crm-surface-alt)', textAlign: 'left' }}>
            {['Клієнт', 'Стадія', 'Менеджер', 'Наступний контакт', 'Завдання', 'Виручка', 'Наступна подія'].map(item => <th key={item} style={{ padding: 10, borderBottom: '1px solid var(--crm-border)' }}>{item}</th>)}
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center' }}>Завантаження…</td></tr>}
            {!loading && customers.map(customer => (
              <tr key={customer.id} onClick={() => openCustomer(customer.id)} style={{ cursor: 'pointer', borderBottom: '1px solid var(--crm-border)', opacity: customer.isActive ? 1 : 0.6, background: customer.isFollowUpOverdue ? 'var(--crm-warning-bg)' : undefined }}>
                <td style={{ padding: 10, fontWeight: 600 }}>{customer.name}<div style={{ fontWeight: 400, fontSize: 12, color: 'var(--crm-text-muted)' }}>{customer.contacts || '—'}</div></td>
                <td style={{ padding: 10 }}><StageBadge code={customer.stageCode} name={customer.stageName} /></td>
                <td style={{ padding: 10 }}>{customer.assignedUserName || '—'}</td>
                <td style={{ padding: 10 }}>
                  {date(customer.nextContactAtUtc)}
                  {customer.isFollowUpOverdue && <div><OverdueBadge count={1} label="Прострочено" /></div>}
                </td>
                <td style={{ padding: 10 }}>
                  {customer.pendingTaskCount}
                  {customer.overdueTaskCount > 0 && <div><OverdueBadge count={customer.overdueTaskCount} label="Протерм." /></div>}
                </td>
                <td style={{ padding: 10 }}>{money(customer.completedRevenue)}</td>
                <td style={{ padding: 10 }}>{date(customer.nextEventDate)}</td>
              </tr>
            ))}
            {!loading && customers.length === 0 && <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: 'var(--crm-text-muted)' }}>Клієнтів не знайдено.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div role="dialog" aria-modal="true" onClick={closeCustomer} className="crm-customer-dialog" style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'grid', placeItems: 'center', padding: 20, background: 'rgba(0,0,0,.5)' }}>
          <div onClick={event => event.stopPropagation()} className="crm-customer-dialog__card" style={{ width: 'min(1020px, 100%)', maxHeight: '92vh', overflow: 'auto', padding: 22, borderRadius: 10, background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', boxShadow: 'var(--crm-shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><h3 style={{ marginTop: 0 }}>Картка клієнта</h3><button onClick={closeCustomer} aria-label="Закрити">×</button></div>

            <div className="crm-customer-edit-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
              <label>Ім’я<input value={edit.name} onChange={event => setEdit(current => ({ ...current, name: event.target.value }))} style={{ ...fieldStyle, display: 'block', width: '100%', padding: 8, boxSizing: 'border-box' }} /></label>
              <label>Контакти<input value={edit.contacts} onChange={event => setEdit(current => ({ ...current, contacts: event.target.value }))} style={{ ...fieldStyle, display: 'block', width: '100%', padding: 8, boxSizing: 'border-box' }} /></label>
              <label>Стадія воронки
                <select value={edit.stageCode} onChange={event => setEdit(current => ({ ...current, stageCode: event.target.value }))} style={{ ...fieldStyle, display: 'block', width: '100%', padding: 8, boxSizing: 'border-box' }}>
                  {CRM_PIPELINE_STAGES.map(stage => <option key={stage} value={stage}>{stageLabel(stage)}</option>)}
                </select>
              </label>
              <label>Відповідальний менеджер
                <select value={edit.assignedUserId} onChange={event => setEdit(current => ({ ...current, assignedUserId: event.target.value }))} style={{ ...fieldStyle, display: 'block', width: '100%', padding: 8, boxSizing: 'border-box' }}>
                  <option value="">Не призначено</option>
                  {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </label>
              <label>Наступний контакт
                <input type="datetime-local" value={edit.nextContactAtUtcLocal} onChange={event => setEdit(current => ({ ...current, nextContactAtUtcLocal: event.target.value }))} style={{ ...fieldStyle, display: 'block', width: '100%', padding: 8, boxSizing: 'border-box' }} />
              </label>
              <label style={{ alignSelf: 'end' }}><input type="checkbox" checked={edit.isActive} onChange={event => setEdit(current => ({ ...current, isActive: event.target.checked }))} /> Активний клієнт</label>
            </div>
            <label style={{ display: 'block', marginTop: 12 }}>CRM-нотатки<textarea value={edit.notes} onChange={event => setEdit(current => ({ ...current, notes: event.target.value }))} rows={3} style={{ ...fieldStyle, display: 'block', width: '100%', padding: 8, boxSizing: 'border-box' }} /></label>
            <button onClick={save} style={buttonStyle('info', { marginTop: 10, padding: '8px 16px' })}>Зберегти картку</button>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, margin: '18px 0' }}>
              {[['Замовлень', selected.orderCount], ['Завершено', selected.completedOrderCount], ['Виручка', money(selected.completedRevenue)], ['Середній чек', money(selected.averageCompletedCheck)], ['Остання подія', date(selected.lastEventDate)], ['Наступна подія', date(selected.nextEventDate)]].map(([label, value]) => (
                <div key={label} style={{ padding: 12, border: '1px solid var(--crm-border)', borderRadius: 7 }}><small style={{ color: 'var(--crm-text-muted)' }}>{label}</small><div style={{ fontWeight: 700, marginTop: 4 }}>{value}</div></div>
              ))}
            </div>

            <h4>Завдання по клієнту</h4>
            {taskError && <div role="alert" style={{ padding: 10, marginBottom: 10, background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 6 }}>{taskError}</div>}

            <div className="crm-customer-task-form" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14, padding: 12, border: '1px dashed var(--crm-border)', borderRadius: 8 }}>
              <label>Назва<input value={taskDraft.title} onChange={event => setTaskDraft(current => ({ ...current, title: event.target.value }))} style={{ ...fieldStyle, display: 'block', padding: 7, minWidth: 180 }} /></label>
              <label>Термін<input type="datetime-local" value={taskDraft.dueAtUtcLocal} onChange={event => setTaskDraft(current => ({ ...current, dueAtUtcLocal: event.target.value }))} style={{ ...fieldStyle, display: 'block', padding: 7 }} /></label>
              <label>Пріоритет
                <select value={taskDraft.priorityCode} onChange={event => setTaskDraft(current => ({ ...current, priorityCode: event.target.value }))} style={{ ...fieldStyle, display: 'block', padding: 7 }}>
                  {CRM_TASK_PRIORITIES.map(p => <option key={p} value={p}>{priorityLabel(p)}</option>)}
                </select>
              </label>
              <label>Виконавець
                <select value={taskDraft.assignedUserId} onChange={event => setTaskDraft(current => ({ ...current, assignedUserId: event.target.value }))} style={{ ...fieldStyle, display: 'block', padding: 7 }}>
                  <option value="">Не призначено</option>
                  {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </label>
              <label style={{ flex: 1, minWidth: 180 }}>Нотатки<input value={taskDraft.notes} onChange={event => setTaskDraft(current => ({ ...current, notes: event.target.value }))} style={{ ...fieldStyle, display: 'block', width: '100%', padding: 7, boxSizing: 'border-box' }} /></label>
              <button onClick={createTask} style={buttonStyle('success', { padding: '8px 16px', height: 36 })}>Додати завдання</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[...selected.tasks].sort((a, b) => Number(a.isCompleted) - Number(b.isCompleted) || Number(b.isOverdue) - Number(a.isOverdue)).map(task => (
                <div key={task.id} style={{
                  padding: 12, borderRadius: 8, border: '1px solid var(--crm-border)',
                  background: task.isCompleted ? 'var(--crm-surface-alt)' : task.isOverdue ? 'var(--crm-danger-bg)' : 'var(--crm-surface)',
                  opacity: task.isCompleted ? 0.7 : 1,
                }}>
                  {editingTaskId === task.id ? (
                    <div className="crm-customer-task-form" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <label>Назва<input value={taskEdit.title} onChange={event => setTaskEdit(current => ({ ...current, title: event.target.value }))} style={{ ...fieldStyle, display: 'block', padding: 7 }} /></label>
                      <label>Термін<input type="datetime-local" value={taskEdit.dueAtUtcLocal} onChange={event => setTaskEdit(current => ({ ...current, dueAtUtcLocal: event.target.value }))} style={{ ...fieldStyle, display: 'block', padding: 7 }} /></label>
                      <label>Пріоритет
                        <select value={taskEdit.priorityCode} onChange={event => setTaskEdit(current => ({ ...current, priorityCode: event.target.value }))} style={{ ...fieldStyle, display: 'block', padding: 7 }}>
                          {CRM_TASK_PRIORITIES.map(p => <option key={p} value={p}>{priorityLabel(p)}</option>)}
                        </select>
                      </label>
                      <label>Виконавець
                        <select value={taskEdit.assignedUserId} onChange={event => setTaskEdit(current => ({ ...current, assignedUserId: event.target.value }))} style={{ ...fieldStyle, display: 'block', padding: 7 }}>
                          <option value="">Не призначено</option>
                          {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                      </label>
                      <label style={{ flex: 1, minWidth: 180 }}>Нотатки<input value={taskEdit.notes} onChange={event => setTaskEdit(current => ({ ...current, notes: event.target.value }))} style={{ ...fieldStyle, display: 'block', width: '100%', padding: 7, boxSizing: 'border-box' }} /></label>
                      <button onClick={() => saveTask(task)} style={buttonStyle('info', { padding: '7px 14px' })}>Зберегти</button>
                      <button onClick={() => setEditingTaskId(null)} style={buttonStyle('muted', { padding: '7px 14px' })}>Скасувати</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontWeight: 600, textDecoration: task.isCompleted ? 'line-through' : undefined }}>{task.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>
                          {priorityLabel(task.priorityCode)} · Термін: {dateTime(task.dueAtUtc)}
                          {task.isOverdue && <span style={{ marginLeft: 8 }}><OverdueBadge count={1} label="Протерміновано" /></span>}
                          {task.assignedUserName && <> · Виконавець: {task.assignedUserName}</>}
                        </div>
                        {task.notes && <div style={{ fontSize: 13, marginTop: 4 }}>{task.notes}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                        <button onClick={() => toggleTaskCompletion(task)} style={buttonStyle(task.isCompleted ? 'muted' : 'success', { padding: '6px 12px' })}>
                          {task.isCompleted ? 'Повернути' : 'Виконано'}
                        </button>
                        <button onClick={() => startEditTask(task)} style={buttonStyle('muted', { padding: '6px 12px' })}>Редагувати</button>
                        <button onClick={() => deleteTask(task)} style={buttonStyle('danger', { padding: '6px 12px' })}>Видалити</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {selected.tasks.length === 0 && <div style={{ padding: 14, textAlign: 'center', color: 'var(--crm-text-muted)' }}>Завдань ще немає.</div>}
            </div>

            <h4 style={{ marginTop: 22 }}>Історія замовлень</h4>
            <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
              <thead><tr>{['Дата', 'Подія', 'Місце', 'Гостей', 'Статус', 'Сума'].map(item => <th key={item} style={{ padding: 8, textAlign: 'left', borderBottom: '1px solid var(--crm-border)' }}>{item}</th>)}</tr></thead>
              <tbody>{selected.orders.map(order => <tr key={order.id} style={{ borderBottom: '1px solid var(--crm-border)' }}>
                <td style={{ padding: 8 }}>{date(order.eventDate)}</td>
                <td style={{ padding: 8 }}><Link to="/management/event-orders" onClick={closeCustomer}>#{order.id} {order.eventName || 'Подія'}</Link></td>
                <td style={{ padding: 8 }}>{order.eventVenue || '—'}</td>
                <td style={{ padding: 8 }}>{order.guestCount}</td>
                <td style={{ padding: 8 }}>{order.orderStatus}</td>
                <td style={{ padding: 8 }}>{money(order.totalPrice)}</td>
              </tr>)}</tbody>
            </table></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmCustomersPage;
